import React, { useState, useRef } from 'react';
import { Send, Image, X, Loader, Settings } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PrivacySettings from './PrivacySettings';
import ContributingArtistSelector from './ContributingArtistSelector';
import api, { uploadApi } from '../services/api';

const PostCreator = ({ profileUsername, onPostCreated }) => {
    const { user, isAuthenticated } = useAuth();
    const [content, setContent] = useState('');
    const [images, setImages] = useState([]);
    const [contributingArtists, setContributingArtists] = useState([]);
    const [isPosting, setIsPosting] = useState(false);
    const [error, setError] = useState(null);
    const [showPrivacySettings, setShowPrivacySettings] = useState(false);
    const [showContributingArtists, setShowContributingArtists] = useState(false);
    const [privacy, setPrivacy] = useState({
        accessLevel: 'public',
        accessPassword: '',
        accessHint: '',
        isPaid: false,
        price: '',
        currency: 'USD'
    });
    const fileInputRef = useRef(null);

    // Don't render if user is not authenticated
    if (!isAuthenticated) {
        return null;
    }

    const handleImageSelect = (event) => {
        const files = Array.from(event.target.files);
        
        // Validate files
        const validFiles = files.filter(file => {
            const isValidType = file.type.startsWith('image/');
            const isValidSize = file.size <= 10 * 1024 * 1024; // 10MB limit
            
            if (!isValidType) {
                setError('Only image files are allowed');
                return false;
            }
            
            if (!isValidSize) {
                setError('Images must be under 10MB');
                return false;
            }
            
            return true;
        });

        // Add to images array (max 10 images)
        const currentCount = images.length;
        const remainingSlots = 10 - currentCount;
        const newImages = validFiles.slice(0, remainingSlots);

        if (newImages.length > 0) {
            const imagePreews = newImages.map(file => ({
                file,
                preview: URL.createObjectURL(file),
                id: Date.now() + Math.random()
            }));

            setImages(prev => [...prev, ...imagePreews]);
            setError(null);

            // Show warning if uploading many images
            if (images.length + newImages.length > 5) {
                setError('Uploading more than 5 images may take longer. Consider posting fewer images at once for faster upload.');
            }
        }

        // Clear the file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const removeImage = (imageId) => {
        setImages(prev => {
            const updated = prev.filter(img => img.id !== imageId);
            // Clean up object URLs
            const removed = prev.find(img => img.id === imageId);
            if (removed) {
                URL.revokeObjectURL(removed.preview);
            }
            return updated;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!content.trim() && images.length === 0) {
            setError('Please enter some content or add an image');
            return;
        }

        setIsPosting(true);
        setError(null);

        try {
            const formData = new FormData();
            formData.append('content', content.trim());
            formData.append('profileUsername', profileUsername);
            formData.append('accessLevel', privacy.accessLevel);
            formData.append('accessPassword', privacy.accessPassword);
            formData.append('accessHint', privacy.accessHint);
            formData.append('price', privacy.isPaid ? privacy.price : '');
            formData.append('currency', privacy.currency);

            // Add images to form data
            images.forEach((image, index) => {
                formData.append('images', image.file);
            });

            // Use uploadApi for posts with images, regular api for text-only posts
            const apiInstance = images.length > 0 ? uploadApi : api;
            const response = await apiInstance.post('/posts', formData, {
                headers: images.length > 0 ? {
                    'Content-Type': 'multipart/form-data',
                } : {
                    'Content-Type': 'application/json',
                }
            });

            if (response.data.success) {
                const createdPost = response.data.data.post;
                
                // Add contributing artists if any are selected
                if (contributingArtists.length > 0) {
                    try {
                        const contributingArtistIds = contributingArtists.map(artist => artist.user_id);
                        await api.post(`/contributing-artists/post/${createdPost.id}`, {
                            contributingArtistIds
                        });
                    } catch (contributingError) {
                        console.error('Error adding contributing artists:', contributingError);
                        // Don't fail the entire post creation for this
                    }
                }
                
                // Clear form
                setContent('');
                setImages([]);
                setContributingArtists([]);
                setPrivacy({
                    accessLevel: 'public',
                    accessPassword: '',
                    accessHint: '',
                    isPaid: false,
                    price: '',
                    currency: 'USD'
                });
                setShowPrivacySettings(false);
                setShowContributingArtists(false);
                
                // Clean up object URLs
                images.forEach(img => URL.revokeObjectURL(img.preview));
                
                // Notify parent component
                if (onPostCreated) {
                    onPostCreated(createdPost);
                }
            } else {
                setError(response.data.message || 'Failed to create post');
            }

        } catch (err) {
            console.error('Error creating post:', err);
            setError(err.response?.data?.message || 'Failed to create post. Please try again.');
        } finally {
            setIsPosting(false);
        }
    };

    return (
        <div className="glass-card p-3 sm:p-4 border border-blue-600/30 mb-4">
            <form onSubmit={handleSubmit} className="space-y-2.5">
                {/* User Info */}
                <div className="flex items-center gap-2 mb-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-600 to-blue-800 rounded-full flex items-center justify-center flex-shrink-0">
                        {user?.profile?.profilePictureUrl ? (
                            <img
                                src={user.profile.profilePictureUrl}
                                alt="Your avatar"
                                width="32"
                                height="32"
                                className="w-8 h-8 rounded-full object-cover"
                                style={{ background: 'transparent' }}
                            />
                        ) : (
                            <span className="text-white font-semibold text-sm">
                                {user?.profile?.displayName?.charAt(0) || user?.profile?.username?.charAt(0) || 'U'}
                            </span>
                        )}
                    </div>
                    <div>
                        <p className="text-sm font-medium text-white leading-tight">
                            {user?.profile?.displayName || user?.profile?.username}
                        </p>
                        <p className="text-xs text-gray-400 leading-tight">
                            Posting to @{profileUsername}'s profile
                        </p>
                    </div>
                </div>

                {/* Text Input */}
                <div>
                    <textarea
                        value={content}
                        onChange={(e) => setContent(e.target.value)}
                        placeholder={`Share something with ${profileUsername === user?.profile?.username ? 'your followers' : '@' + profileUsername}...`}
                        className="w-full bg-gray-800/50 border border-gray-600 rounded-lg p-3 text-white placeholder-gray-400 resize-y min-h-[70px] focus:border-blue-600 focus:ring-1 focus:ring-blue-600 transition-colors"
                        rows={2}
                        maxLength={2000}
                        disabled={isPosting}
                    />
                    <div className="flex justify-end mt-0.5">
                        <span className="text-xs text-gray-500">
                            {content.length}/2000
                        </span>
                    </div>
                </div>

                {/* Image Previews */}
                {images.length > 0 && (
                    <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-300">
                            Attached Images ({images.length}/10)
                        </p>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {images.map((image) => (
                                <div key={image.id} className="relative group">
                                    <img
                                        src={image.preview}
                                        alt="Preview"
                                        className="w-full aspect-square object-cover rounded-lg border border-gray-600"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => removeImage(image.id)}
                                        aria-label="Remove image"
                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center text-white text-xs transition-colors"
                                        disabled={isPosting}
                                    >
                                        <X className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Contributing Artists Section */}
                <div className="border-t border-gray-700/50 pt-2.5">
                    <button
                        type="button"
                        onClick={() => setShowContributingArtists(!showContributingArtists)}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
                        disabled={isPosting}
                    >
                        <span>🎨</span>
                        {contributingArtists.length > 0 ? (
                            <span className="text-blue-400">
                                Contributing Artists ({contributingArtists.length})
                            </span>
                        ) : (
                            <span>Add Contributing Artists</span>
                        )}
                    </button>

                    {showContributingArtists && (
                        <div className="mt-2.5">
                            <ContributingArtistSelector
                                selectedArtists={contributingArtists}
                                onArtistsChange={setContributingArtists}
                                placeholder="Search for artists who contributed to this post..."
                                maxArtists={10}
                                disabled={isPosting}
                            />
                        </div>
                    )}
                </div>

                {/* Privacy Settings Toggle */}
                <div className={`${showContributingArtists ? '' : 'border-t border-gray-700/50'} pt-2.5`}>
                    <button
                        type="button"
                        onClick={() => setShowPrivacySettings(!showPrivacySettings)}
                        className="flex items-center gap-2 text-sm text-gray-400 hover:text-gray-300 transition-colors"
                        disabled={isPosting}
                    >
                        <Settings className="w-4 h-4" />
                        {privacy.accessLevel !== 'public' ? (
                            <span className="text-yellow-400">Privacy: {privacy.accessLevel.replace('_', ' ')}</span>
                        ) : (
                            <span>Privacy Settings</span>
                        )}
                    </button>
                </div>

                {/* Privacy Settings Panel */}
                {showPrivacySettings && (
                    <div className="border border-gray-600 rounded-lg p-3">
                        <PrivacySettings
                            value={privacy.accessLevel}
                            password={privacy.accessPassword}
                            passwordHint={privacy.accessHint}
                            isPaid={privacy.isPaid}
                            price={privacy.price}
                            currency={privacy.currency}
                            onChange={(value) => setPrivacy(prev => ({ ...prev, accessLevel: value }))}
                            onPasswordChange={(password) => setPrivacy(prev => ({ ...prev, accessPassword: password }))}
                            onHintChange={(hint) => setPrivacy(prev => ({ ...prev, accessHint: hint }))}
                            onIsPaidChange={(v) => setPrivacy(prev => ({ ...prev, isPaid: v }))}
                            onPriceChange={(price) => setPrivacy(prev => ({ ...prev, price: price }))}
                            onCurrencyChange={(currency) => setPrivacy(prev => ({ ...prev, currency: currency }))}
                            disabled={isPosting}
                        />
                    </div>
                )}

                {/* Error/Warning Message */}
                {error && (
                    <div className={`border rounded-lg p-3 ${
                        error.includes('take longer') || error.includes('Consider') 
                            ? 'bg-yellow-500/10 border-yellow-500/30' 
                            : 'bg-red-500/10 border-red-500/30'
                    }`}>
                        <p className={`text-sm ${
                            error.includes('take longer') || error.includes('Consider')
                                ? 'text-yellow-400' 
                                : 'text-red-400'
                        }`}>
                            {error}
                        </p>
                    </div>
                )}

                {/* Actions */}
                <div className="flex items-center justify-between pt-1 gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={handleImageSelect}
                            className="hidden"
                            disabled={isPosting || images.length >= 10}
                        />
                        <button
                            type="button"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={isPosting || images.length >= 10}
                            className="flex items-center gap-1.5 px-2.5 py-1.5 bg-gray-700 hover:bg-gray-600 disabled:bg-gray-800 disabled:opacity-50 text-gray-300 rounded-lg transition-colors text-sm flex-shrink-0"
                        >
                            <Image className="w-4 h-4" />
                            <span className="hidden sm:inline">Add Photos</span>
                        </button>
                        {images.length >= 10 && (
                            <span className="text-xs text-yellow-400 truncate">
                                Maximum 10 images
                            </span>
                        )}
                    </div>

                    <button
                        type="submit"
                        disabled={
                            isPosting ||
                            (!content.trim() && images.length === 0) ||
                            ((privacy.accessLevel === 'password_protected' || privacy.accessLevel === 'secret') && !privacy.accessPassword.trim()) ||
                            (privacy.isPaid && (!privacy.price || parseFloat(privacy.price) <= 0))
                        }
                        className="flex items-center gap-2 px-4 py-1.5 bg-gradient-to-r from-blue-700 to-blue-900 hover:from-blue-800 hover:to-gray-900 disabled:from-gray-700 disabled:to-gray-700 disabled:opacity-50 text-white rounded-lg transition-all duration-200 font-medium text-sm flex-shrink-0"
                    >
                        {isPosting ? (
                            <>
                                <Loader className="w-4 h-4 animate-spin" />
                                Posting...
                            </>
                        ) : (
                            <>
                                <Send className="w-4 h-4" />
                                Post
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PostCreator;